// Темы для web-booking
// Переэкспорт из shared для обратной совместимости
import {
  baseThemes as sharedBaseThemes,
  type BaseTheme,
  type ThemeName,
  spacingCss,
  radiusCss,
  elevationCss,
  typographyCss,
  fontWeightCss,
  lineHeightCss,
  overlayCss,
} from '@minibook/shared';
import { SOCIAL_BRAND_TILE_BG } from '../socialBrand.js';
import { devLog } from '../lib/devLog.js';

// Deprecated: используйте baseThemes из @minibook/shared
export type Theme = BaseTheme;
export { type ThemeName };

// Используем темы из @minibook/shared (всегда доступны после импорта)
export const themes = sharedBaseThemes;
export const baseThemes = sharedBaseThemes;

export function applyTheme(themeName: ThemeName = 'light') {
  const validThemeName: ThemeName = (themeName && sharedBaseThemes[themeName]) ? themeName : 'light';
  const theme = sharedBaseThemes[validThemeName] || sharedBaseThemes.light;
  const root = document.documentElement;
  
  if (import.meta.env.DEV) {
    devLog('[applyTheme]', { themeName, validThemeName, theme });
  }
  
  root.style.setProperty('--color-primary', theme.primary);
  root.style.setProperty('--color-primary-hover', theme.primaryHover);
  root.style.setProperty('--color-text-primary', theme.textPrimary);
  root.style.setProperty('--color-text-secondary', theme.textSecondary);
  root.style.setProperty('--color-text-muted', theme.textMuted);
  // textOnPrimary - опциональное поле в BaseTheme
  const textOnPrimary = (theme as any).textOnPrimary || '#FFFFFF';
  root.style.setProperty('--color-text-on-primary', textOnPrimary);
  root.style.setProperty('--color-bg', theme.bg);
  // Поверхность чуть контрастнее: поле поиска, пилли, подложки под табами лучше читаются
  const surfaceContrast: Record<string, string> = {
    light: '#EEEEEE',
    dark: '#252525',
    pink: '#F0E6EC',
    beige: '#EBEBE0',
  };
  root.style.setProperty('--color-surface', surfaceContrast[validThemeName] ?? theme.surface);
  root.style.setProperty('--color-card', theme.card);
  root.style.setProperty('--color-border', theme.border);
  root.style.setProperty('--color-success', theme.success);
  root.style.setProperty('--color-warning', theme.warning);
  root.style.setProperty('--color-danger', theme.danger);
  
  const primaryRgb = hexToRgb(theme.primary);
  if (primaryRgb) {
    root.style.setProperty('--color-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }
  const dangerRgb = hexToRgb(theme.danger);
  if (dangerRgb) {
    root.style.setProperty('--color-danger-rgb', `${dangerRgb.r}, ${dangerRgb.g}, ${dangerRgb.b}`);
    root.style.setProperty('--color-danger-bg', `rgba(${dangerRgb.r}, ${dangerRgb.g}, ${dangerRgb.b}, 0.08)`);
    root.style.setProperty('--color-danger-border', `rgba(${dangerRgb.r}, ${dangerRgb.g}, ${dangerRgb.b}, 0.4)`);
  }
  const warningRgb = hexToRgb(theme.warning);
  if (warningRgb) {
    root.style.setProperty('--color-warning-rgb', `${warningRgb.r}, ${warningRgb.g}, ${warningRgb.b}`);
    root.style.setProperty('--color-warning-bg', `rgba(${warningRgb.r}, ${warningRgb.g}, ${warningRgb.b}, 0.12)`);
    root.style.setProperty('--color-warning-bg-subtle', `rgba(${warningRgb.r}, ${warningRgb.g}, ${warningRgb.b}, 0.05)`);
    root.style.setProperty('--color-warning-border', `rgba(${warningRgb.r}, ${warningRgb.g}, ${warningRgb.b}, 0.5)`);
  }

  applyBookingUiAccents();
}

/** Как web-master applyDesignConfig: цветные «+» из design-config.ts uiAccents.addButtons */
const MASTER_ADD_BG_MIX_PCT = 57;
const MASTER_ADD_BORDER_MIX_PCT = 56;
const MASTER_ADD_ICON_TEXT_MIX_PCT = 82;

const BOOKING_MASTER_ADD_ACCENTS = {
  service: { icon: '#E65C96', bg: '#FDE7F1' },
  product: { icon: '#14B8A6', bg: '#CCFBF1' },
  appointment: { icon: '#3B82F6', bg: '#DBEAFE' },
} as const;

function bookingMixWithCard(bgHex: string, pct: number): string {
  return `color-mix(in srgb, ${bgHex} ${pct}%, var(--color-card) ${100 - pct}%)`;
}

function bookingMixIconWithText(iconHex: string): string {
  return `color-mix(in srgb, ${iconHex} ${MASTER_ADD_ICON_TEXT_MIX_PCT}%, var(--color-text-primary) ${100 - MASTER_ADD_ICON_TEXT_MIX_PCT}%)`;
}

export function applyBookingUiAccents() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const set = (k: string, v: string) => root.style.setProperty(k, v);

  const { service, product, appointment } = BOOKING_MASTER_ADD_ACCENTS;

  set('--booking-accent-service-icon', bookingMixIconWithText(service.icon));
  set('--booking-accent-service-bg', bookingMixWithCard(service.bg, MASTER_ADD_BG_MIX_PCT));
  set('--booking-accent-service-border', bookingMixWithCard(service.bg, MASTER_ADD_BORDER_MIX_PCT));

  set('--booking-accent-product-icon', bookingMixIconWithText(product.icon));
  set('--booking-accent-product-bg', bookingMixWithCard(product.bg, MASTER_ADD_BG_MIX_PCT));
  set('--booking-accent-product-border', bookingMixWithCard(product.bg, MASTER_ADD_BORDER_MIX_PCT));

  set('--booking-accent-appointment-icon', bookingMixIconWithText(appointment.icon));
  set('--booking-accent-appointment-bg', bookingMixWithCard(appointment.bg, MASTER_ADD_BG_MIX_PCT));
  set('--booking-accent-appointment-border', bookingMixWithCard(appointment.bg, MASTER_ADD_BORDER_MIX_PCT));

  /* Размеры как IconButton md в web-master (design-config control.iconButton.md) */
  set('--booking-landing-icon-well-size', '40px');
  set('--booking-landing-icon-well-radius', '10px');

  /* Соцсети: подложка как цветные «+» — пастель + тот же 57/56 mix с --color-card */
  (Object.keys(SOCIAL_BRAND_TILE_BG) as Array<keyof typeof SOCIAL_BRAND_TILE_BG>).forEach((platform) => {
    const tileBg = SOCIAL_BRAND_TILE_BG[platform];
    set(`--booking-social-${platform}-bg`, bookingMixWithCard(tileBg, MASTER_ADD_BG_MIX_PCT));
    set(`--booking-social-${platform}-border`, bookingMixWithCard(tileBg, MASTER_ADD_BORDER_MIX_PCT));
  });
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/** Design tokens из @minibook/shared (UI-KIT). --spacing-*, --radius-*, --shadow-*, --font-size-*, --font-weight-*. */
export function applyDesignTokens() {
  const root = document.documentElement;
  Object.entries(spacingCss).forEach(([k, v]) => root.style.setProperty(`--spacing-${k}`, v));
  Object.entries(radiusCss).forEach(([k, v]) => root.style.setProperty(`--radius-${k}`, v));
  Object.entries(elevationCss).forEach(([k, v]) => root.style.setProperty(`--shadow-${k}`, v));
  Object.entries(typographyCss).forEach(([k, v]) => root.style.setProperty(`--font-size-${k}`, v));
  Object.entries(fontWeightCss).forEach(([k, v]) => root.style.setProperty(`--font-weight-${k}`, v));
  Object.entries(lineHeightCss).forEach(([k, v]) => root.style.setProperty(`--line-height-${k}`, v));
  root.style.setProperty('--overlay-bg', overlayCss.bg);
  // Роли UI-KIT: title 16, body 14, meta 12, micro 11
  root.style.setProperty('--font-size-title', typographyCss.md);
  root.style.setProperty('--font-size-body', typographyCss.sm);
  root.style.setProperty('--font-size-meta', typographyCss.xs);
  root.style.setProperty('--font-size-micro', typographyCss.micro);
}
